import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSite } from '../context/SiteContext';

const STORAGE_KEY = 'fl_cookie_consent';

export default function CookieBanner() {
  const { t } = useSite();
  const [visible, setVisible] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
  }, []);

  const save = (value) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-4 bottom-4 z-[110] mx-auto max-w-2xl border border-zinc-800 bg-zinc-950/95 p-5 backdrop-blur-xl sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[420px]"
          role="dialog"
          aria-label={t.cookie.title}
          data-testid="cookie-banner"
        >
          <p className="font-heading text-sm font-bold uppercase tracking-widest brand-text">{t.cookie.title}</p>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.cookie.text}</p>

          {configuring && (
            <div className="mt-4 space-y-3 border-t border-zinc-800 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">{t.cookie.essential}</p>
                  <p className="text-xs text-zinc-500">{t.cookie.essentialDesc}</p>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t.cookie.alwaysOn}</span>
              </div>
              <label className="flex cursor-pointer items-center justify-between" data-testid="cookie-analytics-toggle">
                <div>
                  <p className="text-sm font-semibold text-white">{t.cookie.analytics}</p>
                  <p className="text-xs text-zinc-500">{t.cookie.analyticsDesc}</p>
                </div>
                <button
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics(!analytics)}
                  className={`relative ml-4 h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${analytics ? 'brand-bg' : 'bg-zinc-700'}`}
                >
                  <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </label>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {configuring ? (
              <button
                onClick={() => save({ essential: true, analytics })}
                data-testid="cookie-save"
                className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-black brand-bg"
              >
                {t.cookie.save}
              </button>
            ) : (
              <>
                <button
                  onClick={() => save({ essential: true, analytics: true })}
                  data-testid="cookie-accept"
                  className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-black brand-bg"
                >
                  {t.cookie.accept}
                </button>
                <button
                  onClick={() => save({ essential: true, analytics: false })}
                  data-testid="cookie-refuse"
                  className="border border-zinc-700 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white transition-colors hover:border-white"
                >
                  {t.cookie.refuse}
                </button>
                <button
                  onClick={() => setConfiguring(true)}
                  data-testid="cookie-configure"
                  className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 transition-colors hover:text-white"
                >
                  {t.cookie.configure}
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
